import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { prospectCommitmentSchema } from '../../lib/validation';
import type { ProspectCommitmentData } from '../../lib/validation';
import { useSponsorshipTiers } from '../../hooks/useSponsorshipTiers';

interface Commitment {
  id: string;
  tier_interest?: string;
  amount_pledged?: number;
  expected_close?: string;
  probability_pct?: number;
  status: string;
  notes?: string;
}

interface ProspectCommitmentSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  prospectId: string;
  commitment?: Commitment | null;
}

const COMMITMENT_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'border-gray-300 text-gray-600 bg-gray-50' },
  { value: 'verbal', label: 'Verbal', color: 'border-blue-300 text-blue-700 bg-blue-50' },
  { value: 'written', label: 'Written', color: 'border-yellow-300 text-yellow-700 bg-yellow-50' },
  { value: 'signed', label: 'Signed', color: 'border-emerald-300 text-emerald-700 bg-emerald-50' },
  { value: 'cancelled', label: 'Cancelled', color: 'border-red-300 text-red-600 bg-red-50' },
] as const;

export default function ProspectCommitmentSheet({ isOpen, onClose, onSuccess, prospectId, commitment }: ProspectCommitmentSheetProps) {
  const isEditing = !!commitment;
  const { tiers } = useSponsorshipTiers();

  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } = useForm<ProspectCommitmentData>({
    resolver: zodResolver(prospectCommitmentSchema),
    defaultValues: { status: 'draft', probability_pct: 50 },
  });

  const status = watch('status');
  const probability = watch('probability_pct') ?? 50;

  useEffect(() => {
    if (commitment) {
      reset({
        tier_interest: commitment.tier_interest || '',
        amount_pledged: commitment.amount_pledged || undefined,
        expected_close: commitment.expected_close || '',
        probability_pct: commitment.probability_pct ?? 50,
        status: commitment.status as ProspectCommitmentData['status'],
        notes: commitment.notes || '',
      });
    } else {
      reset({ status: 'draft', probability_pct: 50 });
    }
  }, [commitment, reset, isOpen]);

  const onSubmit = async (data: ProspectCommitmentData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (isEditing && commitment) {
        const { error } = await supabase.from('prospect_commitments').update({
          tier_interest: data.tier_interest || null,
          amount_pledged: data.amount_pledged ?? null,
          expected_close: data.expected_close || null,
          probability_pct: data.probability_pct ?? null,
          status: data.status,
          notes: data.notes || null,
        }).eq('id', commitment.id);
        if (error) throw error;
        toast.success('Commitment updated');
      } else {
        const { data: newCommitment, error } = await supabase.from('prospect_commitments').insert([{
          prospect_id: prospectId,
          tier_interest: data.tier_interest || null,
          amount_pledged: data.amount_pledged ?? null,
          expected_close: data.expected_close || null,
          probability_pct: data.probability_pct ?? null,
          status: data.status,
          notes: data.notes || null,
          created_by: user?.id,
        }]).select().single();
        if (error) throw error;

        await supabase.from('prospect_activities').insert([{
          prospect_id: prospectId,
          activity_type: 'commitment_added',
          title: `Commitment recorded: ${data.tier_interest || 'Unspecified tier'}`,
          metadata: {
            commitment_id: newCommitment.id,
            amount: data.amount_pledged,
            tier: data.tier_interest,
            status: data.status,
          },
          performed_by: user?.id,
        }]);
        toast.success('Commitment recorded');
      }

      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save commitment');
    }
  };

  const allTiers = tiers;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:scale-95">
              <Dialog.Panel className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                  <Dialog.Title className="text-base font-semibold text-golf-gray">
                    {isEditing ? 'Edit Commitment' : 'Record Commitment'}
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                    <XMarkIcon className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-5 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tier Interest</label>
                    <select {...register('tier_interest')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange bg-white">
                      <option value="">Select tier...</option>
                      {allTiers.map(t => (
                        <option key={t.id} value={t.name}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Amount Pledged (₦)</label>
                      <input type="number" {...register('amount_pledged', { valueAsNumber: true })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" placeholder="0" />
                      {errors.amount_pledged && <p className="mt-1 text-xs text-red-600">{errors.amount_pledged.message}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expected Close</label>
                      <input type="date" {...register('expected_close')} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange" />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">Probability</label>
                      <span className="text-sm font-semibold text-golf-orange">{probability}%</span>
                    </div>
                    <input type="range" min="0" max="100" step="5"
                      value={probability}
                      onChange={e => setValue('probability_pct', parseInt(e.target.value))}
                      className="w-full accent-golf-orange" />
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>0%</span><span>50%</span><span>100%</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Commitment Status</label>
                    <div className="flex flex-wrap gap-2">
                      {COMMITMENT_STATUSES.map(({ value, label, color }) => (
                        <button key={value} type="button" onClick={() => setValue('status', value)}
                          className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${status === value ? color : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea {...register('notes')} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange resize-none" />
                  </div>

                  <div className="flex justify-end gap-3 pt-1">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-golf-orange rounded-lg hover:bg-golf-orange/90 transition-colors disabled:opacity-60">
                      {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Record Commitment'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
